/**
 * Opportunity Information step — single UI module. Branching (composing a request vs persisted `request`)
 * is internal; view-model hooks live in {@link ./useOpportunityInformation}.
 */
import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TouchableOpacity,
} from 'react-native';

import {
  ProjectInformationFormProvider,
  ProjectInformationShell,
  ProjectInformationAdditionalSection,
} from './subsections/ProjectInformation';
import {
  isOpportunityInformationPersistedProps,
  useOpportunityInformationPersisted,
  type OpportunityInformationProps,
  type OpportunityInformationInitialProps,
  type OpportunityInformationPersistedProps,
} from './useOpportunityInformation';
import {
  FormInput,
  SubsectionForm,
  FormRow,
  FormColumn,
  FormFieldStack,
  FormFieldShowInfoIconsProvider,
  formFieldInStack,
} from '../../../components/forms';
import { Eye, EyeOff } from '../../../components/icons';
import { PermissionAwareField } from '../../../components/PermissionAwareField';
import { StepHeader } from '../../../components/StepHeader';
import {
  OpportunityInformationTeam,
  type OpportunityInformationTeamProps,
} from '../../../components/steps/OpportunityInformationTeam';
import { SECTION_IDS } from '../../../constants/sectionIds';
import { colors, spacing, fontSize, fontWeight } from '../../../theme';
import {
  getFieldError,
  validateRequestorPhone,
} from '../../../utils/formValidation';

import type { SelectedFile } from '../../../components/forms/FileUpload';
import type { SavedAttachment } from '../../../services/attachmentService';
import type { Request } from '../../../types';
import type { FormValidationError } from '../../../utils/formValidation';

type OpportunitySubTab = 'project' | 'requestor';

const shellStyles = StyleSheet.create({
  root: { width: '100%' },
  requestorBlock: { marginTop: 0, width: '100%' },
  teamBlock: { marginTop: 0, width: '100%' },
  additionalBlock: { marginTop: 0, width: '100%' },
});

/**
 * Secondary tabs — aligns with Vertiv Visual Template “tab list / horizontal”
 * (label-only row, selected = brand underline + weight; no nested pill card).
 */
const oppSubTabStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'flex-end',
    gap: spacing.md,
    width: '100%',
  },
  tab: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    marginBottom: -StyleSheet.hairlineWidth,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    backgroundColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: colors.primary,
  },
  tabLabel: {
    fontSize: fontSize.formLabel,
    fontWeight: fontWeight.medium,
    color: colors.textSecondary,
  },
  tabLabelActive: {
    color: colors.primary,
    fontWeight: fontWeight.semibold,
  },
});

function OpportunitySubTabBar(props: {
  active: OpportunitySubTab;
  onChange: (tab: OpportunitySubTab) => void;
}): React.ReactElement {
  const tabs: { id: OpportunitySubTab; label: string }[] = [
    { id: 'project', label: 'Project' },
    { id: 'requestor', label: 'Requestor' },
  ];
  return (
    <View
      testID="opportunity-information-sub-tabs"
      style={oppSubTabStyles.row}
      accessibilityRole="tablist"
      accessibilityLabel="Opportunity subsection tabs"
    >
      {tabs.map(t => (
        <TouchableOpacity
          key={t.id}
          style={[
            oppSubTabStyles.tab,
            props.active === t.id && oppSubTabStyles.tabActive,
          ]}
          onPress={() => props.onChange(t.id)}
          accessibilityRole="tab"
          accessibilityLabel={`Opportunity ${t.label} tab`}
          accessibilityState={{ selected: props.active === t.id }}
        >
          <Text
            style={[
              oppSubTabStyles.tabLabel,
              props.active === t.id && oppSubTabStyles.tabLabelActive,
            ]}
          >
            {t.label}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );
}

const persistedOppHeaderStyles = StyleSheet.create({
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Platform.OS === 'web' ? 12 : 8,
    paddingVertical: Platform.OS === 'web' ? 6 : 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f8f9fa',
  },
  viewToggleText: {
    fontSize: Platform.OS === 'web' ? 13 : 11,
    fontWeight: '500',
    color: '#666',
  },
});

interface RequestorFieldsInitialProps {
  formData: Record<string, unknown>;
  updateFormData: (
    field: string,
    value: string | number | boolean | string[] | unknown[],
  ) => void;
  requestId?: string;
  validationErrors?: FormValidationError[];
  disabled?: boolean;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  /** When true, omit page-level header (nested under opportunity information). */
  embeddedUnderInformation?: boolean;
}

const RequestorFieldsInitial: React.FC<RequestorFieldsInitialProps> = ({
  formData,
  updateFormData,
  requestId,
  validationErrors = [],
  disabled = false,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  embeddedUnderInformation = false,
}) => {
  const stage = String(formData.stage ?? formData.sysRequestStage ?? 'DRAFT');
  const [phoneBlurError, setPhoneBlurError] = useState<string | undefined>();
  const phoneValue = String(formData.requestorPhone ?? '');
  const phoneValueRef = useRef(phoneValue);
  phoneValueRef.current = phoneValue;
  const phoneError =
    getFieldError(validationErrors, 'requestorPhone') ?? phoneBlurError;

  const requestorRows = (
    <>
      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="requestorName"
            requestStage={stage}
            requestId={requestId}
          >
            <FormInput
              testID="field-requestor-name"
              label="Requestor Name"
              value={String(formData.requestorName ?? '')}
              onChangeText={value => updateFormData('requestorName', value)}
              placeholder="Enter the name of the person requesting this assessment"
              required
              error={getFieldError(validationErrors, 'requestorName')}
              disabled={disabled}
              readOnlyMode={disabled}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
        </FormColumn>
        <FormColumn>
          <PermissionAwareField
            fieldId="requestorRole"
            requestStage={stage}
            requestId={requestId}
          >
            <FormInput
              testID="field-requestor-role"
              label="Requestor Role"
              value={String(formData.requestorRole ?? '')}
              onChangeText={value => updateFormData('requestorRole', value)}
              placeholder="Enter requestor's job title or role"
              error={getFieldError(validationErrors, 'requestorRole')}
              disabled={disabled}
              readOnlyMode={disabled}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="requestorEmail"
            requestStage={stage}
            requestId={requestId}
          >
            <FormInput
              testID="field-requestor-email"
              label="Requestor Email"
              value={String(formData.requestorEmail ?? '')}
              onChangeText={value => updateFormData('requestorEmail', value)}
              placeholder="Enter requestor's email address"
              required
              error={getFieldError(validationErrors, 'requestorEmail')}
              disabled={disabled}
              readOnlyMode={disabled}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
        </FormColumn>
        <FormColumn>
          <PermissionAwareField
            fieldId="requestorPhone"
            requestStage={stage}
            requestId={requestId}
          >
            <FormInput
              testID="field-requestor-phone"
              label="Requestor Phone"
              value={phoneValue}
              onChangeText={value => {
                updateFormData('requestorPhone', value);
                setPhoneBlurError(undefined);
              }}
              onBlur={() =>
                setPhoneBlurError(validateRequestorPhone(phoneValueRef.current))
              }
              placeholder="Enter requestor's phone number"
              keyboardType="phone-pad"
              maxLength={50}
              hideCharacterCount
              error={phoneError}
              disabled={disabled}
              readOnlyMode={disabled}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>
    </>
  );

  return (
    <View
      style={requestorInitialStyles.container}
      accessibilityLabel={
        embeddedUnderInformation
          ? 'Create request requestor subsection'
          : 'Create request requestor step'
      }
    >
      {embeddedUnderInformation ? null : (
        <StepHeader title="Requestor" variant="title-only" />
      )}

      <FormFieldShowInfoIconsProvider showIcons={false}>
        {embeddedUnderInformation ? (
          <FormFieldStack>{requestorRows}</FormFieldStack>
        ) : (
          <SubsectionForm
            containerStyle={{ marginBottom: spacing.pdStepSubsectionGap }}
            title="Requestor Information"
            sectionId={SECTION_IDS.OPPORTUNITY_REQUESTOR}
            requestId={requestId}
            allowAttachments
            attachments={
              attachmentsBySection[SECTION_IDS.OPPORTUNITY_REQUESTOR] || []
            }
            onAttachFiles={() =>
              onAttachFiles?.(SECTION_IDS.OPPORTUNITY_REQUESTOR)
            }
            onDeleteAttachment={onDeleteAttachment}
            isEditing={!disabled}
            disabled={disabled}
            selectedFiles={
              selectedFiles[SECTION_IDS.OPPORTUNITY_REQUESTOR] || []
            }
            onFilesSelected={
              onFilesSelected
                ? files =>
                    onFilesSelected(SECTION_IDS.OPPORTUNITY_REQUESTOR, files)
                : undefined
            }
            isOffline={offlineMode}
          >
            {requestorRows}
          </SubsectionForm>
        )}
      </FormFieldShowInfoIconsProvider>
    </View>
  );
};

interface RequestorFieldsPersistedProps {
  request: Request;
  isEditing: boolean;
  editedData: Record<string, unknown>;
  updateEditedData: (
    field: string,
    value: string | string[] | number | boolean,
  ) => void;
  showFullDetails?: boolean;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
  disabled?: boolean;
  embeddedUnderInformation?: boolean;
}

const RequestorFieldsPersisted: React.FC<RequestorFieldsPersistedProps> = ({
  request,
  isEditing,
  editedData,
  updateEditedData,
  showFullDetails = true,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  onCaptionChange,
  pendingCaptionEdits,
  disabled = false,
  embeddedUnderInformation = false,
}) => {
  const fieldDisabled = !isEditing || disabled;
  const [phoneBlurError, setPhoneBlurError] = useState<string | undefined>();
  const phoneValue = String(
    isEditing
      ? editedData.requestorPhone ??
          editedData.phone ??
          request.requestorPhone ??
          request.phone ??
          ''
      : request.requestorPhone ?? request.phone ?? '',
  );
  const phoneValueRef = useRef(phoneValue);
  phoneValueRef.current = phoneValue;
  const phoneError = phoneBlurError;

  const requestorRows = (
    <>
      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="requestorName"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <FormInput
              testID="field-requestor-name"
              label="Requestor Name"
              value={
                isEditing
                  ? String(
                      editedData.requestorName ?? request.requestorName ?? '',
                    )
                  : String(request.requestorName ?? '')
              }
              onChangeText={
                isEditing
                  ? value => updateEditedData('requestorName', value)
                  : () => {}
              }
              placeholder="Enter the name of the person requesting this assessment"
              required
              disabled={!isEditing}
              readOnlyMode={!isEditing}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
        </FormColumn>
        <FormColumn>
          <PermissionAwareField
            fieldId="requestorRole"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <FormInput
              testID="field-requestor-role"
              label="Requestor Role"
              value={
                isEditing
                  ? String(
                      editedData.requestorRole ?? request.requestorRole ?? '',
                    )
                  : String(request.requestorRole ?? '')
              }
              onChangeText={
                isEditing
                  ? value => updateEditedData('requestorRole', value)
                  : () => {}
              }
              disabled={!isEditing}
              readOnlyMode={!isEditing}
              placeholder="Enter requestor's job title or role"
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="requestorEmail"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <FormInput
              testID="field-requestor-email"
              label="Requestor Email"
              value={
                isEditing
                  ? String(
                      editedData.requestorEmail ?? request.requestorEmail ?? '',
                    )
                  : String(request.requestorEmail ?? '')
              }
              onChangeText={
                isEditing
                  ? value => updateEditedData('requestorEmail', value)
                  : () => {}
              }
              placeholder="Enter requestor's email address"
              required
              disabled={!isEditing}
              readOnlyMode={!isEditing}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
        </FormColumn>
        <FormColumn>
          <PermissionAwareField
            fieldId="requestorPhone"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <FormInput
              testID="field-requestor-phone"
              label="Requestor Phone"
              value={phoneValue}
              onChangeText={
                isEditing
                  ? value => {
                      updateEditedData('requestorPhone', value);
                      setPhoneBlurError(undefined);
                    }
                  : () => {}
              }
              onBlur={
                isEditing
                  ? () =>
                      setPhoneBlurError(
                        validateRequestorPhone(phoneValueRef.current),
                      )
                  : undefined
              }
              disabled={!isEditing}
              readOnlyMode={!isEditing}
              placeholder="Enter requestor's phone number"
              keyboardType="phone-pad"
              maxLength={50}
              hideCharacterCount
              error={phoneError}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>
    </>
  );

  return (
    <View
      style={requestorPersistedStyles.container}
      accessibilityLabel={
        embeddedUnderInformation
          ? 'Request detail requestor subsection'
          : 'Request detail requestor step'
      }
    >
      {embeddedUnderInformation ? null : (
        <View style={requestorPersistedStyles.headerRow}>
          <Text style={requestorPersistedStyles.pageTitle}>Requestor</Text>
        </View>
      )}

      <FormFieldShowInfoIconsProvider showIcons={false}>
        {embeddedUnderInformation ? (
          <FormFieldStack>{requestorRows}</FormFieldStack>
        ) : (
          <SubsectionForm
            containerStyle={{ marginBottom: spacing.pdStepSubsectionGap }}
            title="Requestor Information"
            sectionId={SECTION_IDS.OPPORTUNITY_REQUESTOR}
            requestId={request.id}
            allowAttachments
            attachments={
              attachmentsBySection[SECTION_IDS.OPPORTUNITY_REQUESTOR] || []
            }
            onAttachFiles={() =>
              onAttachFiles?.(SECTION_IDS.OPPORTUNITY_REQUESTOR)
            }
            onDeleteAttachment={onDeleteAttachment}
            isEditing={isEditing}
            disabled={fieldDisabled}
            selectedFiles={
              selectedFiles[SECTION_IDS.OPPORTUNITY_REQUESTOR] || []
            }
            onFilesSelected={
              onFilesSelected
                ? files =>
                    onFilesSelected(SECTION_IDS.OPPORTUNITY_REQUESTOR, files)
                : undefined
            }
            isOffline={offlineMode}
            onCaptionChange={onCaptionChange}
            pendingCaptionEdits={pendingCaptionEdits}
          >
            {requestorRows}
          </SubsectionForm>
        )}
      </FormFieldShowInfoIconsProvider>
    </View>
  );
};

const OpportunityInformationInitialLayout: React.FC<
  OpportunityInformationInitialProps
> = props => {
  const [oppSubTab, setOppSubTab] = useState<OpportunitySubTab>('project');

  return (
    <View
      style={shellStyles.root}
      accessibilityLabel="Create request opportunity step"
    >
      <ProjectInformationFormProvider
        {...props}
        opportunityInformationSubTab={oppSubTab}
        projectInformationTopTabs={
          <OpportunitySubTabBar active={oppSubTab} onChange={setOppSubTab} />
        }
        opportunityProjectRequestorSlot={
          <View style={shellStyles.requestorBlock}>
            <RequestorFieldsInitial {...props} embeddedUnderInformation />
          </View>
        }
      >
        <ProjectInformationShell />
        <View
          style={shellStyles.teamBlock}
          accessibilityLabel="Create request team information block"
        >
          <OpportunityInformationTeam
            formData={
              props.formData as OpportunityInformationTeamProps['formData']
            }
            updateFormData={props.updateFormData}
            requestId={props.requestId}
            validationErrors={props.validationErrors}
            disabled={props.disabled}
            attachmentsBySection={props.attachmentsBySection}
            onAttachFiles={props.onAttachFiles}
            onDeleteAttachment={props.onDeleteAttachment}
            isEditing={props.isEditing}
            selectedFiles={props.selectedFiles}
            onFilesSelected={props.onFilesSelected}
            offlineMode={props.offlineMode}
          />
        </View>
        <View style={shellStyles.additionalBlock}>
          <ProjectInformationAdditionalSection />
        </View>
      </ProjectInformationFormProvider>
    </View>
  );
};

const OpportunityInformationPersistedLayout: React.FC<
  OpportunityInformationPersistedProps
> = props => {
  const vm = useOpportunityInformationPersisted(props);
  const [oppSubTab, setOppSubTab] = useState<OpportunitySubTab>('project');

  const headerUomRowEnd =
    props.showViewToggle && props.onToggleView ? (
      <TouchableOpacity
        style={persistedOppHeaderStyles.viewToggleButton}
        onPress={props.onToggleView}
        accessibilityRole="button"
      >
        {props.isAdvancedView ? (
          <Eye size={Platform.OS === 'web' ? 18 : 16} color="#666" />
        ) : (
          <EyeOff size={Platform.OS === 'web' ? 18 : 16} color="#666" />
        )}
        <Text style={persistedOppHeaderStyles.viewToggleText}>
          {props.isAdvancedView ? 'Advanced' : 'Basic'}
        </Text>
      </TouchableOpacity>
    ) : null;

  return (
    <View
      style={shellStyles.root}
      accessibilityLabel="Request detail opportunity step"
    >
      <ProjectInformationFormProvider
        key={`detail-opp-pd-${vm.request.id}-${vm.requestProjectDetailsSnapshot}`}
        formData={vm.formData}
        updateFormData={vm.updateFormData}
        validationErrors={[]}
        disabled={vm.fieldDisabled}
        requestId={vm.requestIdStr}
        attachmentsBySection={vm.attachmentsBySection}
        onAttachFiles={vm.onAttachFiles}
        onDeleteAttachment={vm.onDeleteAttachment}
        isEditing={vm.isEditing}
        selectedFiles={vm.selectedFiles}
        onFilesSelected={vm.onFilesSelected}
        offlineMode={vm.offlineMode}
        parentSyncRevision={vm.parentSyncRevision}
        headerUomRowEnd={headerUomRowEnd}
        opportunityInformationSubTab={oppSubTab}
        projectInformationTopTabs={
          <OpportunitySubTabBar active={oppSubTab} onChange={setOppSubTab} />
        }
        opportunityProjectRequestorSlot={
          <View style={shellStyles.requestorBlock}>
            <RequestorFieldsPersisted
              request={vm.request}
              isEditing={vm.isEditing}
              editedData={vm.editedData}
              updateEditedData={vm.updateEditedData}
              showFullDetails={vm.showFullDetails}
              attachmentsBySection={vm.attachmentsBySection}
              onAttachFiles={vm.onAttachFiles}
              onDeleteAttachment={vm.onDeleteAttachment}
              selectedFiles={vm.selectedFiles}
              onFilesSelected={vm.onFilesSelected}
              offlineMode={vm.offlineMode}
              onCaptionChange={vm.onCaptionChange}
              pendingCaptionEdits={vm.pendingCaptionEdits}
              disabled={vm.disabled}
              embeddedUnderInformation
            />
          </View>
        }
      >
        <ProjectInformationShell />
        <View
          style={shellStyles.teamBlock}
          accessibilityLabel="Request detail team information block"
        >
          <OpportunityInformationTeam
            formData={vm.formData}
            updateFormData={vm.updateFormData}
            requestId={vm.requestIdStr}
            validationErrors={[]}
            disabled={vm.fieldDisabled}
            attachmentsBySection={vm.attachmentsBySection}
            onAttachFiles={vm.onAttachFiles}
            onDeleteAttachment={vm.onDeleteAttachment}
            isEditing={vm.isEditing}
            selectedFiles={vm.selectedFiles}
            onFilesSelected={vm.onFilesSelected}
            offlineMode={vm.offlineMode}
            teamHydrationWritesFormData={false}
          />
        </View>
        <View style={shellStyles.additionalBlock}>
          <ProjectInformationAdditionalSection />
        </View>
      </ProjectInformationFormProvider>
    </View>
  );
};

export const OpportunityInformation: React.FC<
  OpportunityInformationProps
> = props =>
  isOpportunityInformationPersistedProps(props) ? (
    <OpportunityInformationPersistedLayout {...props} />
  ) : (
    <OpportunityInformationInitialLayout {...props} />
  );

OpportunityInformation.displayName = 'OpportunityInformation';

const requestorInitialStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

const requestorPersistedStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
    flexWrap: 'wrap',
    gap: 12,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ff6b35',
    marginBottom: spacing.belowFormStepPageTitle,
    flexShrink: 1,
  },
  pdfWrap: {
    marginBottom: 8,
  },
});
